import type { Node as ProseMirrorNode } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import type { EditorContext } from "../context";
import { nodes } from "../schema";

/**
 * §16 — the trailing empty paragraph. The document always ends in a paragraph
 * or heading so there is somewhere to click below the last block. This runs
 * **only in an editable view**: a read-only view that appends one generates a
 * phantom edit on every page view, which the spec calls out as the single most
 * likely regression here.
 */
export function trailingParagraphPlugin(context: EditorContext): Plugin {
  return new Plugin({
    key: new PluginKey("wikiTrailingParagraph"),
    appendTransaction: (transactions, _oldState, newState) => {
      if (context.readOnly) return null;
      if (!transactions.some((tr) => tr.docChanged)) return null;
      if (!needsTrailingParagraph(newState.doc)) return null;
      const paragraph = nodes.paragraph.createAndFill();
      if (!paragraph) return null;
      return newState.tr.insert(newState.doc.content.size, paragraph);
    },
  });
}

/** Also applied when normalizing stored content on load. */
export function needsTrailingParagraph(doc: ProseMirrorNode): boolean {
  const last = doc.lastChild;
  if (!last) return true;
  if (last.type === nodes.heading) return false;
  if (last.type !== nodes.paragraph) return true;
  // A paragraph holding only a lone image is not somewhere you can type.
  return last.childCount > 0 && !last.content.child(0).isText;
}

/** The same invariant applied once to stored content as it is loaded. */
export function withTrailingParagraph(doc: ProseMirrorNode): ProseMirrorNode {
  if (!needsTrailingParagraph(doc)) return doc;
  const paragraph = nodes.paragraph.createAndFill();
  if (!paragraph) return doc;
  return doc.copy(doc.content.addToEnd(paragraph));
}

/**
 * §16 — the maximum length. A transaction that would push the document past
 * the cap is rejected outright; the keystroke simply does nothing, and a
 * rejected paste surfaces a notice rather than failing silently.
 */
export function maxLengthPlugin(context: EditorContext): Plugin {
  return new Plugin({
    key: new PluginKey("wikiMaxLength"),
    filterTransaction: (tr) => {
      if (!tr.docChanged) return true;
      if (tr.doc.nodeSize <= context.maxLength) return true;
      context.onNotice("This document has reached its maximum length.");
      return false;
    },
  });
}

/** §2 — the placeholder shown in an empty document. */
export function placeholderPlugin(context: EditorContext): Plugin {
  return new Plugin({
    key: new PluginKey("wikiPlaceholder"),
    props: {
      decorations(state) {
        if (context.placeholder === "" || context.readOnly) return DecorationSet.empty;
        const doc = state.doc;
        const first = doc.firstChild;
        const isEmpty =
          doc.childCount <= 1 &&
          first !== null &&
          first.type === nodes.paragraph &&
          first.content.size === 0;
        if (!isEmpty) return DecorationSet.empty;
        return DecorationSet.create(doc, [
          Decoration.node(0, first.nodeSize, {
            class: "wiki-editor__placeholder",
            "data-placeholder": context.placeholder,
          }),
        ]);
      },
    },
  });
}

/**
 * Presentation flags the stylesheet reads: read-only, full width, and whether
 * code blocks show line numbers (a per-user preference, not a node attribute).
 */
export function editorAttributesPlugin(context: EditorContext): Plugin {
  return new Plugin({
    key: new PluginKey("wikiEditorAttributes"),
    props: {
      attributes: () => ({
        class: [
          "wiki-editor__surface",
          context.fullWidth ? "wiki-editor__surface--full-width" : "",
          context.readOnly ? "wiki-editor__surface--read-only" : "",
          context.lineNumbers ? "wiki-editor__surface--line-numbers" : "",
        ]
          .filter((entry) => entry !== "")
          .join(" "),
        role: "textbox",
        "aria-multiline": "true",
        ...(context.readOnly ? { "aria-readonly": "true" } : {}),
      }),
      editable: () => !context.readOnly,
    },
  });
}
